import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { MediaField, GalleryField, ChaptersField } from "./MediaFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  slug: "",
  title: "",
  org: "",
  tags: [],
  summary: "",
  scale: "",
  status: "Published",
  order: 0,
  published: true,
  hero_image: null,
  gallery: [],
  chapters: [],
};

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/case-studies`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await axios.patch(`${API}/admin/case-studies/${id}`, body, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/admin/case-studies`, payload, { withCredentials: true });
        toast.success("Created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-case-studies">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Case Studies</h1>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing({ ...BLANK, order: items.length + 1 }); }}
          data-testid="cs-add"
          className="btn-primary"
        >
          <Plus size={14} /> New case study
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : (
        <div className="mt-8 border border-[#343437]">
          {items.length === 0 && (
            <div className="p-8 text-[#A9A9A6] text-sm">No case studies yet.</div>
          )}
          {items.map((c) => (
            <div
              key={c.id}
              className="border-t border-[#343437] first:border-t-0 p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-[80px_1fr_140px_200px] gap-4 items-center"
              data-testid={`cs-row-${c.slug}`}
            >
              <span className="oversize text-2xl text-[#D8A936]">{String(c.order).padStart(2, "0")}</span>
              <div>
                <p className="font-display text-[17px] text-[#FAF9F6]">{c.title}</p>
                <p className="text-xs text-[#A9A9A6] mt-1">{c.org} · {c.tags.join(" · ")}</p>
              </div>
              <span className={`text-[10px] tracking-[0.22em] uppercase ${c.published ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>
                {c.published ? c.status : "Unpublished"}
              </span>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setEditing(c)}
                  data-testid={`cs-edit-${c.slug}`}
                  className="px-3 py-1.5 border border-[#343437] text-xs tracking-widest uppercase hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`cs-delete-${c.slug}`}
                  className="p-1.5 border border-[#343437] hover:border-red-400 hover:text-red-400 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal
          initial={editing}
          isNew={creating}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditorModal({ initial, isNew, onCancel, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
    hero_image: initial.hero_image || null,
    gallery: Array.isArray(initial.gallery) ? initial.gallery : [],
    chapters: Array.isArray(initial.chapters) ? initial.chapters : [],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    // Validation — every media asset must have alt text (empty-alt = don't render rule)
    if (form.hero_image && !form.hero_image.alt?.trim()) {
      toast.error("Hero image requires alt text.");
      return;
    }
    const badGallery = (form.gallery || []).findIndex((g) => g.src && !g.alt?.trim());
    if (badGallery !== -1) {
      toast.error(`Gallery image ${badGallery + 1} requires alt text.`);
      return;
    }
    const badChapter = (form.chapters || []).findIndex((c) => c.image?.src && !c.image?.alt?.trim());
    if (badChapter !== -1) {
      toast.error(`Chapter ${badChapter + 1} image requires alt text.`);
      return;
    }
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      order: Number(form.order) || 0,
      // Drop media rows with no src (partially-filled empty rows)
      gallery: (form.gallery || []).filter((g) => g?.src),
      chapters: (form.chapters || []).filter((c) => c?.title || c?.body),
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0B0B0C]/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#151517] border border-[#343437] w-full max-w-3xl p-8 space-y-5 relative my-8"
        data-testid="cs-editor"
      >
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close">
          <X size={18} />
        </button>
        <p className="eyebrow">{isNew ? "New" : "Edit"}</p>
        <h2 className="font-display text-2xl">Case study</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Slug" value={form.slug} onChange={(v) => set("slug", v)} required testid="cs-slug" />
          <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="cs-order" />
        </div>
        <TextField label="Title" value={form.title} onChange={(v) => set("title", v)} required testid="cs-title" />
        <TextField label="Organisation" value={form.org} onChange={(v) => set("org", v)} required testid="cs-org" />
        <TextField label="Tags (comma-separated)" value={form.tags} onChange={(v) => set("tags", v)} testid="cs-tags" />
        <TextArea label="Summary" value={form.summary} onChange={(v) => set("summary", v)} required testid="cs-summary" />
        <TextField label="Scale" value={form.scale} onChange={(v) => set("scale", v)} testid="cs-scale" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Status label" value={form.status} onChange={(v) => set("status", v)} testid="cs-status" />
          <label className="flex items-center gap-3 mt-6 text-sm text-[#FAF9F6]">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="accent-[#D8A936]"
              data-testid="cs-published"
            />
            Published (visible on public site)
          </label>
        </div>

        {/* Batch 2 §4 — Media schema fields. Empty state = nothing renders on
            the public case-study page (§3.2). Alt text is enforced client-side
            before save so no image can ship without a screen-reader label. */}
        <div className="pt-4 border-t border-[#343437] space-y-6">
          <div>
            <p className="eyebrow mb-3">Hero</p>
            <MediaField
              label="Hero image (optional — leave empty to render no hero)"
              value={form.hero_image}
              onChange={(v) => set("hero_image", v)}
              testid="cs-hero"
              aspect="aspect-[4/5]"
            />
          </div>
          <GalleryField
            label="Gallery"
            value={form.gallery}
            onChange={(v) => set("gallery", v)}
            testid="cs-gallery"
          />
          <ChaptersField
            label="Chapters"
            value={form.chapters}
            onChange={(v) => set("chapters", v)}
            testid="cs-chapters"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="cs-save" className="btn-primary">
            <Save size={14} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <textarea
        rows={4}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936] resize-none"
      />
    </label>
  );
}
